import isNil from 'lodash/isNil';
import orderBy from 'lodash/orderBy';
import { useMemo, useState } from 'react';
import { useUpdateEffect } from 'react-use';
const getCellValue = (row, column) => { var _a; return (_a = row.cells.find((cell) => cell.column === column.key)) === null || _a === void 0 ? void 0 : _a.value; };
/**
 * A hook for handling client-side sorting and paginating of rows in Analytics
 * tables.
 *
 * The hook expects the table instrument data including the columns and rows, as
 * well as the active sorted column, and returns the rows available on the
 * current page, as well as the metadata and handlers for paginating.
 */
export const useTableInstrumentRows = ({ data: { columns, rows }, limit = 50, sortStatus, }) => {
    const initialPage = 1;
    const totalPages = Math.ceil(rows.length / limit);
    const [currentPage, setCurrentPage] = useState(initialPage);
    // Reset the page when the sort status or number of total pages changes.
    useUpdateEffect(() => {
        if (currentPage !== 1) {
            setCurrentPage(initialPage);
        }
    }, [sortStatus, totalPages]);
    /**
     * The list of all rows, sorted by the active column. If no column is actively
     * sorted, rows are returned in the original order received from the API.
     */
    const sortedRows = useMemo(() => {
        if (!sortStatus)
            return rows;
        const column = columns.find(({ key }) => key === sortStatus.sortBy);
        if (!column)
            return rows;
        return orderBy(rows, [
            (row) => !isNil(getCellValue(row, column)),
            (row) => getCellValue(row, column),
        ], ['desc', sortStatus.direction]);
    }, [columns, rows, sortStatus]);
    /** The list of rows visible on the current page. */
    const currentRows = useMemo(() => sortedRows.slice((currentPage - 1) * limit, currentPage * limit), [currentPage, limit, sortedRows]);
    return {
        rows: currentRows,
        currentPage,
        totalPages,
        setPage: setCurrentPage,
    };
};
